import React from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { Modal, Input, Button, Table, Alert } from "antd";
import CaseService from "services/case-service";
import { formatCurrency } from "utilities/currency";

const DASH = "—";

const fmtAmount = value =>
    value === null || value === undefined || value === ""
        ? DASH
        : `₹ ${formatCurrency(value, true)}`;

const fmtDateTime = raw => {
    if (!raw) {
        return DASH;
    }
    const m = moment(raw);
    return m.isValid() ? m.format("DD MMM YYYY, hh:mm A") : String(raw);
};

const HISTORY_COLUMNS = [
    {
        title: "Old Value",
        key: "old_value",
        render: r => fmtAmount(r.old_value)
    },
    {
        title: "New Value",
        key: "new_value",
        render: r => fmtAmount(r.new_value)
    },
    {
        title: "Remark",
        key: "remark",
        render: r => r.remark || DASH
    },
    {
        title: "Updated On",
        key: "create_date",
        render: r => fmtDateTime(r.create_date)
    },
    {
        title: "Updated By",
        key: "created_by",
        render: r => r.created_by || DASH
    }
];

/**
 * Limit tracker — shared by the Limit Assessment card's Sales Proposed Limit
 * and Proposed Limit tiles. Both are opened from an edit or view icon (the
 * icon/edit-gate itself is decided by the caller — stage-based for Sales
 * Proposed Limit, permission-based for Proposed Limit) and both are backed by
 * the same generic `case/<id>/limit/` endpoint, discriminated by `limitType`
 * ("SALES_PROPOSED_LIMIT" / "PROPOSED_LIMIT").
 *
 * `canEdit`: an editable value + a mandatory remark (the audit reason) and a
 * Submit button, via CaseService.updateLimit.
 * Not `canEdit`: read-only — just the history below.
 *
 * History is always shown, server-paginated via CaseService.getLimitHistory,
 * filtered to this `limitType`.
 */
class LimitTrackerModal extends React.Component {
    state = {
        value: "",
        remark: "",
        submitting: false,
        errors: {},
        history: [],
        historyCount: 0,
        historyPage: 1,
        historyLoading: false
    };

    componentDidUpdate(prevProps) {
        if (this.props.visible && !prevProps.visible) {
            this.setState({
                value:
                    this.props.currentValue !== null &&
                    this.props.currentValue !== undefined
                        ? String(this.props.currentValue)
                        : "",
                remark: "",
                errors: {},
                historyPage: 1
            });
            this.fetchHistory(1);
        }
    }

    fetchHistory = page => {
        this.setState({ historyLoading: true });
        CaseService.getLimitHistory(this.props.caseId, {
            limit_type: this.props.limitType,
            page
        })
            .then(({ data }) => {
                const body = data || {};
                this.setState({
                    history: body.results || [],
                    historyCount: body.count || 0,
                    historyPage: page,
                    historyLoading: false
                });
            })
            .catch(() => {
                this.setState({ history: [], historyCount: 0, historyLoading: false });
            });
    };

    onValueChange = e => this.setState({ value: e.target.value });

    onRemarkChange = e => this.setState({ remark: e.target.value });

    submit = () => {
        const { value, remark } = this.state;
        const numeric = Number(String(value).replace(/,/g, ""));
        if (!value || isNaN(numeric)) {
            this.setState({
                errors: { value: ["A valid number is required."] }
            });
            return;
        }
        if (numeric <= 0) {
            this.setState({
                errors: { value: [`${this.props.fieldLabel} cannot be zero.`] }
            });
            return;
        }
        if (!remark || !remark.trim()) {
            this.setState({
                errors: { remark: ["This field may not be blank."] }
            });
            return;
        }
        this.setState({ submitting: true, errors: {} });
        this.props
            .onSubmit(numeric, remark.trim())
            .then(() => {
                this.setState({ submitting: false, remark: "" });
                this.fetchHistory(1);
            })
            .catch(err => {
                const errors =
                    (err && err.response && err.response.data) || {};
                this.setState({ submitting: false, errors });
            });
    };

    fieldError(key) {
        const errs = this.state.errors[key];
        return errs && errs.length ? errs.join(" ") : null;
    }

    renderEditor() {
        const nonFieldError =
            this.fieldError("non_field_errors") || this.fieldError("limit_type");
        return (
            <div className={"cc-splt__editor"}>
                {nonFieldError ? (
                    <Alert
                        type={"error"}
                        message={nonFieldError}
                        showIcon
                        style={{ marginBottom: 12 }}
                    />
                ) : null}
                <div className={"cc-splt__field"}>
                    <label className={"cc-splt__label"}>
                        {this.props.fieldLabel}
                    </label>
                    <Input
                        prefix={"₹"}
                        value={this.state.value}
                        onChange={this.onValueChange}
                    />
                    {this.fieldError("value") ? (
                        <div className={"cc-splt__error"}>
                            {this.fieldError("value")}
                        </div>
                    ) : null}
                </div>
                <div className={"cc-splt__field"}>
                    <label className={"cc-splt__label"}>
                        Remark <span className={"cc-splt__required"}>*</span>
                    </label>
                    <Input.TextArea
                        rows={3}
                        placeholder={"Reason for this update (required)"}
                        value={this.state.remark}
                        onChange={this.onRemarkChange}
                    />
                    {this.fieldError("remark") ? (
                        <div className={"cc-splt__error"}>
                            {this.fieldError("remark")}
                        </div>
                    ) : null}
                </div>
                <Button
                    type={"primary"}
                    loading={this.state.submitting}
                    onClick={this.submit}
                >
                    Submit
                </Button>
            </div>
        );
    }

    render() {
        const { visible, onClose, canEdit, title } = this.props;
        const { history, historyCount, historyPage, historyLoading } = this.state;
        return (
            <Modal
                visible={visible}
                onCancel={onClose}
                footer={null}
                width={720}
                destroyOnClose
                className={"cc-modal cc-splt-modal"}
                title={title}
            >
                {canEdit ? this.renderEditor() : null}
                <div className={"cc-splt__history"}>
                    <div className={"cc-splt__history-title"}>History</div>
                    <Table
                        className={"cc-pf-antd"}
                        rowKey={(r, i) => `${r.create_date}-${i}`}
                        columns={HISTORY_COLUMNS}
                        dataSource={history}
                        loading={historyLoading}
                        size={"small"}
                        scroll={{ x: true }}
                        pagination={{
                            current: historyPage,
                            pageSize: 20,
                            total: historyCount,
                            onChange: p => this.fetchHistory(p),
                            showSizeChanger: false,
                            size: "small"
                        }}
                    />
                </div>
            </Modal>
        );
    }
}

LimitTrackerModal.propTypes = {
    visible: PropTypes.bool,
    caseId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    // "SALES_PROPOSED_LIMIT" | "PROPOSED_LIMIT" — sent as-is to the API and
    // used to filter the history list.
    limitType: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    // Field label shown above the value input ("Sales Proposed Limit" /
    // "Proposed Limit").
    fieldLabel: PropTypes.string.isRequired,
    // Whether the editor (value + remark + Submit) shows at all — decided by
    // the caller (stage-based for Sales Proposed Limit, permission-based for
    // Proposed Limit). false renders history only.
    canEdit: PropTypes.bool,
    currentValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    onSubmit: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired
};

export default LimitTrackerModal;
